import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertCircle,
  Check,
  Globe,
  Key,
  Loader2,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Search,
  Shield,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import {
  useDeleteRole,
  usePermissionsGrouped,
  useRoles,
  useUpdateRole,
} from '../../services/httpServices/queries';
import type { Role, Permission } from '../../services/httpServices/userService';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { usePermissions, resolvePermissionRoutes } from '../../hooks/usePermissions';
import { cn, formatModuleLabel, formatCategoryLabel, groupByCategory } from '../../lib/utils';

type Tab = 'roles' | 'permissions';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'system', label: 'Dashboard roles' },
  { value: 'website', label: 'Website-only roles' },
];

function formatPermissionCount(count: number) {
  return `${count} permission${count === 1 ? '' : 's'}`;
}

function StatCard({
  label,
  value,
  icon: Icon,
  tone = 'primary',
}: {
  label: string;
  value: number | string;
  icon: React.ElementType;
  tone?: 'primary' | 'info' | 'success';
}) {
  const toneClasses = {
    primary: 'bg-primary/10 text-primary',
    info: 'bg-info/10 text-info',
    success: 'bg-success/10 text-success',
  }[tone];

  return (
    <div className="bg-surface border border-border rounded-xl p-4 flex items-center gap-3">
      <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', toneClasses)}>
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-bold text-text leading-tight">{value}</p>
        <p className="text-xs text-text-muted truncate">{label}</p>
      </div>
    </div>
  );
}

/**
 * Read-only permission card used by the catalog. Also renders the app routes a
 * permission unlocks so admins can see what a checkbox actually grants.
 */
function CatalogPermissionCard({ perm }: { perm: Permission }) {
  const routes = resolvePermissionRoutes(perm.code);
  return (
    <div className="flex items-start gap-2 p-2 rounded-lg bg-background/50 border border-border">
      <Check size={14} className="text-success mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-text">{perm.name}</p>
        <p className="text-xs text-text-muted font-mono truncate">{perm.code}</p>
        {routes.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {routes.map((route) => (
              <span
                key={route}
                className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-primary/10 text-primary"
              >
                {route}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Roles & Permissions screen.
 *
 * Lists the custom roles that can be assigned to users (split into dashboard
 * roles and website-only roles used to tag blog authors), and exposes the
 * read-only permission catalog. Mirrors the Website module's list screens for
 * search, filtering, pagination and confirm dialogs.
 */
export function Roles() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const { hasPermission } = usePermissions();
  const canCreateRole = hasPermission('roles:create');
  const canUpdateRole = hasPermission('roles:update');
  const canDeleteRole = hasPermission('roles:delete');
  const canViewPermissions = hasPermission('permissions:read');

  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const t = params.get('tab');
    return t === 'permissions' && canViewPermissions ? 'permissions' : 'roles';
  });

  // Roles tab state
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState(() => {
    const t = params.get('type');
    return t === 'website' || t === 'system' ? t : '';
  });
  const [pendingDelete, setPendingDelete] = useState<Role | null>(null);

  // Permission catalog state
  const [permissionSearch, setPermissionSearch] = useState('');

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      status: status || undefined,
      websiteOnly: type === 'website' ? true : type === 'system' ? false : undefined,
    }),
    [debouncedSearch, status, type],
  );

  const { data: rolesData, isLoading, isFetching, error } = useRoles(page, 20, filters);
  const { data: permissionsData } = usePermissionsGrouped(canViewPermissions);
  const deleteRole = useDeleteRole();
  const updateRole = useUpdateRole();

  const roles = rolesData?.data ?? [];
  const meta = rolesData?.meta;
  const permissionsGrouped = useMemo(() => permissionsData?.data || {}, [permissionsData]);

  // Sync the tab to the URL so the view is shareable/bookmarkable.
  useEffect(() => {
    const newParams = new URLSearchParams(location.search);
    if (activeTab === 'permissions') newParams.set('tab', 'permissions');
    else newParams.delete('tab');
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [activeTab, navigate, location.pathname, location.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [status, type]);

  const permissionCount = useMemo(
    () => Object.values(permissionsGrouped).reduce((sum, list) => sum + (Array.isArray(list) ? list.length : 0), 0),
    [permissionsGrouped],
  );

  // Permission catalog search. Matches the module label, permission name/code
  // and the app routes the permission unlocks, so searching a page URL such as
  // `/billing/cashbook` surfaces the permission that grants access to it.
  const filteredPermissionsGrouped = useMemo(() => {
    const query = permissionSearch.trim().toLowerCase();
    if (!query) return permissionsGrouped;
    const result: Record<string, Permission[]> = {};
    for (const [module, list] of Object.entries(permissionsGrouped)) {
      const perms: Permission[] = Array.isArray(list) ? list : [];
      const matched = perms.filter((perm) => {
        const haystack = [
          perm.name,
          perm.code,
          module,
          formatModuleLabel(module),
          ...resolvePermissionRoutes(perm.code),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      });
      if (matched.length > 0) result[module] = matched;
    }
    return result;
  }, [permissionsGrouped, permissionSearch]);

  const filteredPermissionCount = useMemo(
    () =>
      Object.values(filteredPermissionsGrouped).reduce(
        (sum, list) => sum + (Array.isArray(list) ? list.length : 0),
        0,
      ),
    [filteredPermissionsGrouped],
  );

  const websiteRoleCount = roles.filter((r) => r.isWebsiteOnly).length;
  const hasFilters = !!debouncedSearch || !!status || !!type;
  const isInitialLoading = isLoading && roles.length === 0;

  const handleToggleActive = async (role: Role) => {
    try {
      await updateRole.mutateAsync({ id: role.id, data: { isActive: !role.isActive } });
      toast.success(role.isActive ? 'Role deactivated' : 'Role activated');
    } catch {
      toast.error('Failed to update role');
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteRole.mutateAsync(pendingDelete.id);
      toast.success('Role deleted');
      setPendingDelete(null);
      // If we emptied the last page, step back a page.
      if (roles.length === 1 && page > 1) setPage((p) => p - 1);
    } catch {
      toast.error('Failed to delete role');
    }
  };

  const allTabs: { key: Tab; label: string; icon: React.ElementType }[] = [
    { key: 'roles', label: 'Roles', icon: Shield },
    { key: 'permissions', label: 'Permission Catalog', icon: Key },
  ];
  const tabs = allTabs.filter((tab) => tab.key !== 'permissions' || canViewPermissions);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text">Roles</h1>
            <p className="text-sm text-text-muted">
              Define custom roles and the permissions they grant, then assign them to users.
            </p>
          </div>
        </div>
        {activeTab === 'roles' && canCreateRole && (
          <button
            onClick={() => navigate('/roles/new')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            Add role
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total roles" value={meta?.total ?? roles.length} icon={Shield} />
        <StatCard label="Website-only roles (blog authors)" value={websiteRoleCount} icon={Globe} tone="info" />
        {canViewPermissions && (
          <StatCard label="Available permissions" value={permissionCount} icon={Key} tone="success" />
        )}
      </div>

      {/* Tabs */}
      <div className="flex bg-surface border border-border rounded-lg overflow-hidden w-full sm:w-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium flex-1 sm:flex-none justify-center transition-colors',
              activeTab === tab.key
                ? 'bg-primary text-white'
                : 'text-text-muted hover:text-text hover:bg-background',
            )}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'roles' ? (
        <>
          {/* Website-only explainer */}
          <div className="flex items-start gap-2 p-3 bg-info/10 border border-info/20 rounded-lg text-info text-sm">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <p>
              <span className="font-semibold">Website-only roles</span> do not grant dashboard access. They are
              used to mark employees as selectable authors when writing website blog posts. Assign roles to
              users from the{' '}
              <button
                onClick={() => navigate('/users')}
                className="font-semibold underline hover:text-info-dark transition-colors"
              >
                Users
              </button>{' '}
              page.
            </p>
          </div>

          {/* Filters */}
          <div className="bg-surface rounded-xl border border-border p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search by role name or description…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-9 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-danger"
                    aria-label="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 sm:w-52"
              >
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 sm:w-44"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isInitialLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : error && roles.length === 0 ? (
            <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
              <AlertCircle size={18} />
              <span>Failed to load roles. Please try again.</span>
            </div>
          ) : roles.length === 0 ? (
            <div className="bg-surface rounded-xl border border-dashed border-border py-16 text-center">
              <Shield className="w-10 h-10 text-text-muted mx-auto mb-3" />
              <p className="text-text font-medium">
                {hasFilters ? 'No roles match your filters.' : 'No roles yet. Create your first role to get started.'}
              </p>
              {hasFilters ? (
                <button
                  onClick={() => {
                    setSearch('');
                    setStatus('');
                    setType('');
                  }}
                  className="mt-2 text-sm text-primary hover:underline"
                >
                  Clear filters
                </button>
              ) : canCreateRole ? (
                <button
                  onClick={() => navigate('/roles/new')}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
                >
                  <Plus size={16} />
                  Add role
                </button>
              ) : null}
            </div>
          ) : (
            <div className="bg-surface rounded-xl border border-border overflow-hidden">
              {isFetching && (
                <div className="flex items-center gap-2 px-4 py-2 border-b border-border text-xs text-text-muted">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Updating…
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-background/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Permissions</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {roles.map((role) => (
                      <tr key={role.id} className="hover:bg-background/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div
                              className={cn(
                                'w-8 h-8 rounded-full flex items-center justify-center shrink-0',
                                role.isWebsiteOnly ? 'bg-info/10' : 'bg-primary/10',
                              )}
                            >
                              {role.isWebsiteOnly ? (
                                <Globe size={14} className="text-info" />
                              ) : (
                                <Shield size={14} className="text-primary" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <button
                                onClick={() => canUpdateRole && navigate(`/roles/${role.id}/edit`)}
                                disabled={!canUpdateRole}
                                className="text-sm font-medium text-text hover:text-primary text-left disabled:cursor-default disabled:hover:text-text"
                              >
                                {role.name}
                              </button>
                              {role.isWebsiteOnly && (
                                <span className="ml-2 px-2 py-0.5 bg-info/10 text-info rounded text-xs inline-flex items-center gap-1 align-middle">
                                  <Globe size={11} /> Website only
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-text-muted max-w-[260px]">
                          <span className="line-clamp-2">{role.description || '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          {role.isWebsiteOnly ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-background border border-border rounded text-xs text-text-muted"
                              title="Website-only roles never grant dashboard access, so they hold no dashboard permissions."
                            >
                              <Globe size={11} className="shrink-0" />
                              No dashboard permissions
                            </span>
                          ) : canUpdateRole ? (
                            <button
                              onClick={() => navigate(`/roles/${role.id}/permissions`)}
                              className="px-2 py-0.5 bg-secondary/10 text-secondary rounded text-xs hover:bg-secondary/20 transition-colors"
                              title="Manage permissions"
                            >
                              {formatPermissionCount(role.permissions?.length ?? 0)}
                            </button>
                          ) : (
                            <span className="text-xs text-text-muted">
                              {formatPermissionCount(role.permissions?.length ?? 0)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              'inline-flex px-2 py-1 rounded-full text-xs font-medium',
                              role.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger',
                            )}
                          >
                            {role.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {!role.isWebsiteOnly && canUpdateRole && (
                              <button
                                onClick={() => navigate(`/roles/${role.id}/permissions`)}
                                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-secondary transition-colors"
                                title="Manage permissions"
                              >
                                <Key size={16} />
                              </button>
                            )}
                            {canUpdateRole && (
                              <button
                                onClick={() => handleToggleActive(role)}
                                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                                title={role.isActive ? 'Deactivate role' : 'Activate role'}
                              >
                                {role.isActive ? <PowerOff size={16} /> : <Power size={16} />}
                              </button>
                            )}
                            {canUpdateRole && (
                              <button
                                onClick={() => navigate(`/roles/${role.id}/edit`)}
                                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                                title="Edit role"
                              >
                                <Pencil size={16} />
                              </button>
                            )}
                            {canDeleteRole && (
                              <button
                                onClick={() => setPendingDelete(role)}
                                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                                title="Delete role"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={page}
                totalPages={meta?.totalPages || 1}
                total={meta?.total || 0}
                limit={20}
                onPageChange={setPage}
                itemName="roles"
              />
            </div>
          )}
        </>
      ) : (
        /* Permissions catalog */
        <div className="space-y-4">
          {/* Search */}
          <div className="bg-surface rounded-xl border border-border p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                placeholder="Search permissions by name, code, or page (e.g. /billing/cashbook)…"
                value={permissionSearch}
                onChange={(e) => setPermissionSearch(e.target.value)}
                className="w-full pl-9 pr-9 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              {permissionSearch && (
                <button
                  onClick={() => setPermissionSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-danger"
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <p className="mt-2 text-xs text-text-muted">
              {permissionSearch.trim()
                ? `${filteredPermissionCount} permission${filteredPermissionCount === 1 ? '' : 's'} match "${permissionSearch.trim()}"`
                : `${permissionCount} permissions across ${Object.keys(permissionsGrouped).length} modules`}
            </p>
          </div>

          {Object.entries(filteredPermissionsGrouped).map(([module, perms]: [string, Permission[]]) => (
            <div key={module} className="bg-surface rounded-xl border border-border p-4">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={16} className="text-primary" />
                <h3 className="text-sm font-semibold text-text uppercase tracking-wider">
                  {formatModuleLabel(module)}
                </h3>
                <span className="text-xs text-text-muted">({Array.isArray(perms) ? perms.length : 0})</span>
              </div>
              <div className="space-y-4">
                {groupByCategory(Array.isArray(perms) ? perms : []).map((section) => (
                  <div key={section.category ?? module}>
                    {section.category && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                          {formatCategoryLabel(section.category)}
                        </span>
                        <span className="flex-1 h-px bg-border" />
                        <span className="text-[11px] text-text-muted">
                          {section.permissions.length}
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {section.permissions.map((perm) => (
                        <CatalogPermissionCard key={perm.id} perm={perm} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {Object.keys(filteredPermissionsGrouped).length === 0 && (
            <div className="text-center py-12 text-text-muted bg-surface border border-border rounded-xl">
              {Object.keys(permissionsGrouped).length > 0
                ? 'No permissions match your search.'
                : 'No permissions found'}
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title={`Delete role "${pendingDelete?.name ?? ''}"?`}
        description="Users assigned this role will lose it. This cannot be undone."
        confirmLabel="Delete role"
        loading={deleteRole.isPending}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
